import React, { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import { BrowserRouter, useNavigate, useLocation } from 'react-router-dom';
import ErrorBoundary from './components/ErrorBoundary';
import StoryList from './components/StoryList';
import StoryViewer from './components/StoryViewer';
import WriteStory from './components/WriteStory';
import GenreSelector from './components/GenreSelector';
import AuthButtons from './components/AuthButtons';
import YouTubePlayer from './components/YouTubePlayer';
import AdminDashboard from './components/AdminDashboard';
import AuthorDashboard from './components/AuthorDashboard';
import AuthorStories from './components/AuthorStories';
import BrowseAuthors from './components/BrowseAuthors';
import Competition from './components/Competition';
import Footer from './components/Footer';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfService from './components/TermsOfService';
import ContactUs from './components/ContactUs';
import AppealPage from './components/AppealPage';
import SEO from './components/SEO';
import './App.css';

const API_URL = process.env.REACT_APP_API_URL || '/api';
const PAYPAL_DONATE_URL = 'https://www.paypal.com/ncp/payment/WKSNR77WNYPFS';
const SITE_URL = 'https://storymeal.com';

/**
 * Story page meta tag generator (ShortStory schema with CreativeWork fallback).
 * Builds the JSON-LD schema payload for an individual story.
 */
function buildStorySchema(story, slugPath) {
  const canonical = `${SITE_URL}${slugPath}`;
  const description = (story.content_text || '').slice(0, 155).trim();

  return {
    '@context': 'https://schema.org',
    // Use array so Google can fall back to CreativeWork if ShortStory isn't fully recognised
    '@type': ['ShortStory', 'CreativeWork'],
    headline: story.title || 'Untitled',
    name: story.title || 'Untitled',
    description,
    url: canonical,
    mainEntityOfPage: { '@type': 'WebPage', '@id': canonical },
    inLanguage: 'en',
    author: {
      '@type': 'Person',
      name: story.author || 'Anonymous'
    },
    publisher: {
      '@type': 'Organization',
      name: 'StoryMeal',
      url: SITE_URL,
      logo: { '@type': 'ImageObject', url: `${SITE_URL}/StoryMealLogo2.png` }
    },
    datePublished: story.created_at || story.publish_date || new Date().toISOString(),
    dateModified: story.updated_at || story.created_at || story.publish_date || new Date().toISOString(),
    genre: story.genre || undefined,
    keywords: [story.genre, story.author, story.title].filter(Boolean).join(', ')
  };
}

/**
 * Story BreadcrumbList schema.
 */
function buildStoryBreadcrumb(story, slugPath) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
      story.genre ? {
        '@type': 'ListItem',
        position: 2,
        name: story.genre,
        item: `${SITE_URL}/genre/${encodeURIComponent(story.genre)}`
      } : null,
      {
        '@type': 'ListItem',
        position: story.genre ? 3 : 2,
        name: story.title,
        item: `${SITE_URL}${slugPath}`
      }
    ].filter(Boolean)
  };
}

/**
 * Main App component wrapped in BrowserRouter for SEO-friendly URLs.
 * Uses React Router for proper crawlable URLs instead of hash-based routing.
 */
function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
    </ErrorBoundary>
  );
}

/**
 * Inner app content that uses router hooks.
 */
function AppContent() {
  const navigate = useNavigate();
  const location = useLocation();

  const [currentView, setCurrentView] = useState('home');
  const [selectedGenre, setSelectedGenre] = useState(null);
  const [selectedStory, setSelectedStory] = useState(null);
  const [stories, setStories] = useState([]);
  const [genres, setGenres] = useState([]);
  const [trending, setTrending] = useState([]);
  const [recent, setRecent] = useState([]);
  const [storySearchQuery, setStorySearchQuery] = useState("");
  const [siteStats, setSiteStats] = useState({ total_stories: 0, active_readers: 0, writers: 0 });
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [selectedAuthor, setSelectedAuthor] = useState(null);
  const [storySeoExtra, setStorySeoExtra] = useState({}); // breadcrumb + Story-specific ItemList schema
  const menuRef = useRef(null);
  const modalRef = useRef(null);
  const genreRowsRef = useRef(null);

  // Genre icon lookup (used by the genre dropdown).
  const iconFor = (name) => {
    const map = {
      'Fantasy': '🧙', 'Science Fiction': '🚀', 'Romance': '💕',
      'Mystery': '🔍', 'Horror': '👻', 'Adventure': '🗺️',
      'Drama': '🎭', 'Comedy': '😄', 'Poetry': '📝',
      'Fan Fiction': '⭐', 'Folk': '🏮', 'Fables': '🦊',
      'Spiritual': '🕊️', 'Inspirational': '🌟',
      'Children Stories': '🧸', 'Short Stories': '📘', 'General': '📚'
    };
    return map[name] || '📖';
  };

  // Client-side search filter for the Browse Stories view. Case-insensitive
  // substring match across title, author, and genre.
  const filteredStories = React.useMemo(() => {
    if (!storySearchQuery || !storySearchQuery.trim()) return stories;
    const q = storySearchQuery.trim().toLowerCase();
    return stories.filter(s => {
      const title = (s.title || '').toLowerCase();
      const author = (s.author || '').toLowerCase();
      const genre = (s.genre || '').toLowerCase();
      return title.includes(q) || author.includes(q) || genre.includes(q);
    });
  }, [stories, storySearchQuery]);

  // Sync URL with current view
  useEffect(() => {
    const path = location.pathname;
    if (path === '/' || path === '') {
      setCurrentView('home');
      setSelectedGenre(null);
    } else if (path === '/browse') {
      setCurrentView('read');
      setSelectedGenre(null);
    } else if (path.startsWith('/genre/')) {
      const genre = decodeURIComponent(path.replace('/genre/', ''));
      setSelectedGenre(genre);
      setCurrentView('read');
    } else if (path.startsWith('/story/')) {
      // Don't reset selectedStory here; it triggers a re-fetch.
      setCurrentView('story');
    } else if (path === '/authors') {
      setCurrentView('authors');
    } else if (path.startsWith('/author/')) {
      const author = decodeURIComponent(path.replace('/author/', ''));
      setSelectedAuthor(author);
      setCurrentView('author-stories');
    } else if (path === '/author-dashboard') {
      setCurrentView('author-dashboard');
    } else if (path === '/admin') {
      setCurrentView('admin');
    } else if (path === '/write') {
      setCurrentView('write');
    } else if (path === '/privacy') {
      setCurrentView('privacy');
    } else if (path === '/terms') {
      setCurrentView('terms');
    } else if (path === '/contact') {
      setCurrentView('contact');
    } else if (path === '/competition') {
      setCurrentView('competition');
    } else if (path === '/appeal' || path === '/support') {
      setCurrentView('appeal');
    }
  }, [location.pathname]);

  // Backward compatibility: redirect old hash URLs to proper URLs
  useEffect(() => {
    if (location.hash && location.hash.startsWith('#')) {
      const hash = location.hash.slice(1);
      let newPath = '/';
      if (hash === 'write') newPath = '/write';
      else if (hash === 'browse') newPath = '/browse';
      else if (hash === 'home' || hash === '') newPath = '/';
      else if (hash.startsWith('genre/')) newPath = `/${hash}`;
      else if (hash.startsWith('story/')) newPath = `/${hash}`;
      else if (hash === 'authors') newPath = '/authors';
      else if (hash.startsWith('author/')) newPath = `/${hash}`;
      else if (hash === 'author-dashboard') newPath = '/author-dashboard';
      else if (hash === 'admin') newPath = '/admin';
      else if (hash === 'privacy') newPath = '/privacy';
      else if (hash === 'terms') newPath = '/terms';
      else if (hash === 'contact') newPath = '/contact';
      else if (hash === 'competition') newPath = '/competition';
      else if (hash === 'appeal' || hash === 'support') newPath = '/appeal';

      if (newPath !== '/') {
        navigate(newPath, { replace: true });
      }
    }
  }, [location.hash, navigate]);

  // Scroll helpers
  const scrollToStoryList = () => {
    setTimeout(() => {
      const storyList = document.querySelector('.genre-header')
        || document.querySelector('.story-list')
        || document.querySelector('.stories-grid')
        || document.querySelector('.story-sections');
      if (storyList) {
        storyList.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 150);
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const scrollToWriteStory = () => {
    setTimeout(() => {
      const heading = document.querySelector('.write-story h2');
      if (heading) {
        heading.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const scrollToAuthors = () => {
    setTimeout(() => {
      const heading = document.querySelector('.browse-authors-header h1');
      if (heading) {
        heading.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const fetchAllStories = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/stories?limit=100`);
      if (response.data && response.data.success) {
        setStories(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching all stories:', error);
    } finally {
      setLoading(false);
    }
  };

  // Navigation handlers
  const handleHomeClick = () => {
    setCurrentView('home');
    setSelectedGenre(null);
    navigate('/');
    scrollToTop();
  };

  const handleBrowseClick = () => {
    setCurrentView('read');
    setSelectedGenre(null);
    fetchAllStories();
    navigate('/browse');
    scrollToStoryList();
  };

  const handleWriteClick = () => {
    setCurrentView('write');
    navigate('/write');
    scrollToWriteStory();
  };

  const handleAdminClick = () => {
    setCurrentView('admin');
    setShowUserMenu(false);
    navigate('/admin');
    scrollToTop();
  };

  const handleAuthorDashboardClick = () => {
    setCurrentView('author-dashboard');
    setShowUserMenu(false);
    navigate('/author-dashboard');
    scrollToTop();
  };

  const handleDeleteStory = (storyId) => {
    setStories(prev => prev.filter(s => s.story_id !== storyId && s.id !== storyId));
    setTrending(prev => prev.filter(s => s.story_id !== storyId && s.id !== storyId));
    setRecent(prev => prev.filter(s => s.story_id !== storyId && s.id !== storyId));
    setSelectedStory(null);
    setCurrentView('home');
    navigate('/');
  };

  const handleViewAuthor = (author) => {
    setSelectedAuthor(author);
    setCurrentView('author-stories');
    navigate(`/author/${encodeURIComponent(author)}`);
    scrollToTop();
  };

  const handleBrowseAuthorsClick = () => {
    setCurrentView('authors');
    navigate('/authors');
    scrollToAuthors();
  };

  const handleCompetitionClick = () => {
    setCurrentView('competition');
    navigate('/competition');
    scrollToTop();
  };

  const handlePrivacyClick = () => {
    setCurrentView('privacy');
    navigate('/privacy');
    scrollToTop();
  };

  const handleTermsClick = () => {
    setCurrentView('terms');
    navigate('/terms');
    scrollToTop();
  };

  const handleContactClick = () => {
    setCurrentView('contact');
    navigate('/contact');
    scrollToTop();
  };

  const handleAppealClick = () => {
    setCurrentView('appeal');
    navigate('/appeal');
    scrollToTop();
  };

  const fetchGenres = async () => {
    try {
      const response = await axios.get(`${API_URL}/genres`);
      if (response.data && response.data.success) {
        setGenres(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching genres:', error);
    }
  };

  const fetchTrending = async () => {
    try {
      const response = await axios.get(`${API_URL}/trending`);
      if (response.data && response.data.success) {
        setTrending(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching trending:', error);
    }
  };

  const fetchRecent = async () => {
    try {
      const response = await axios.get(`${API_URL}/recent`);
      if (response.data && response.data.success) {
        setRecent(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching recent:', error);
    }
  };

  const fetchSiteStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/stats`);
      if (response.data && response.data.success && response.data.data) {
        const apiData = response.data.data;
        setSiteStats({
          total_stories: apiData.totalStories ?? 0,
          active_readers: apiData.totalUsers ?? 0,
          writers: apiData.writers ?? 0
        });
      }
    } catch (error) {
      console.error('Error fetching site stats:', error);
      const totalAuthors = new Set([
        ...recent.map(s => s.author).filter(Boolean),
        ...trending.map(s => s.author).filter(Boolean)
      ]).size;
      setSiteStats({
        total_stories: recent.length + trending.length,
        active_readers: 0,
        writers: totalAuthors
      });
    }
  };

  const fetchStoriesByGenre = async (genre) => {
    setLoading(true);
    setSelectedGenre(genre);
    setCurrentView('read');
    navigate(`/genre/${encodeURIComponent(genre)}`);
    try {
      const response = await axios.get(`${API_URL}/genres/${genre}/stories`);
      if (response.data && response.data.success) {
        setStories(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching stories:', error);
    } finally {
      setLoading(false);
    }
    scrollToStoryList();
  };

  // Look up story by either slug OR story_id, then redirect to slug URL.
  const viewStory = async (identifier) => {
    try {
      // First try slug endpoint (cheap). If 404, try by-id endpoint.
      let response = await axios.get(`${API_URL}/stories/by-slug/${encodeURIComponent(identifier)}`)
        .catch(() => null);
      if (!response || !response.data || !response.data.success) {
        response = await axios.get(`${API_URL}/stories/${encodeURIComponent(identifier)}`);
      }
      if (response && response.data && response.data.success) {
        const story = response.data.data;
        setSelectedStory(story);
        setCurrentView('story');
        // Always navigate via slug for SEO-friendly URL
        const slugPath = story.slug ? `/story/${story.slug}` : `/story/${story.story_id}`;
        navigate(slugPath);
        scrollToTop();

        // Build per-story JSON-LD schemas (BreadcrumbList, ShortStory).
        const breadcrumb = buildStoryBreadcrumb(story, slugPath);
        const main = buildStorySchema(story, slugPath);
        setStorySeoExtra({ main, breadcrumb, slugPath });
      }
    } catch (error) {
      console.error('Error fetching story:', error);
    }
  };

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API_URL}/auth/user`, { withCredentials: true });
      if (response.data.success && response.data.user) {
        setUser(response.data.user);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
    } finally {
      setAuthLoading(false);
    }
  };

  const handleLoginClick = () => {
    setShowLoginModal(true);
  };

  const handleLogin = (provider) => {
    window.location.href = `${API_URL}/auth/${provider}`;
  };

  const handleLogout = async () => {
    try {
      await axios.get(`${API_URL}/auth/logout`, { withCredentials: true });
      setUser(null);
      setShowUserMenu(false);
      window.location.reload();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  // Deep-link loader: when the URL is /story/<slug> or /story/<legacy_id> on initial load
  // (or when pasted into a new tab), fetch the story from the API. Without this, opening
  // a direct link lands on the story view but selectedStory is null, so nothing renders.
  useEffect(() => {
    const path = location.pathname;
    if (!path.startsWith('/story/')) return;
    const identifier = decodeURIComponent(path.replace('/story/', ''));
    if (!identifier) return;

    // If identifier already looks like a slug (no underscores, or non-legacy format),
    // assume it's a slug. Otherwise (legacy story_id like "story_1737_xxxxx"), look up
    // the slug and replace the URL.
    const looksLikeLegacyId = /^story_\d+_[a-z0-9]+$/i.test(identifier);

    if (looksLikeLegacyId) {
      let cancelled = false;
      (async () => {
        try {
          const res = await axios.get(`${API_URL}/stories/${encodeURIComponent(identifier)}/slug`);
          if (cancelled) return;
          if (res.data && res.data.success && res.data.data && res.data.data.slug) {
            navigate(`/story/${encodeURIComponent(res.data.data.slug)}`, { replace: true });
          } else {
            // Fallback: try to load directly by id so the user still sees the story
            viewStory(identifier);
          }
        } catch (e) {
          // leave URL alone; the page will fail gracefully
        }
      })();
      return () => { cancelled = true; };
    }

    // Looks like a slug: load the story. Skip if we already have it loaded.
    if (selectedStory && (selectedStory.slug === identifier || selectedStory.story_id === identifier)) {
      return;
    }
    viewStory(identifier);
  }, [location.pathname, navigate, selectedStory]);

  useEffect(() => {
    checkAuth();
    fetchGenres();
    fetchTrending();
    fetchRecent();
    fetchSiteStats();

    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
      if (modalRef.current && !modalRef.current.contains(event.target) && showLoginModal) {
        setShowLoginModal(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showLoginModal]);

 /* // Top banner ad
  const topBannerAd = {
    title: "✨ New: Folk & Fables Collection!",
    description: "Discover timeless tales and magical stories from around the world",
    cta: "Explore Now →",
    link: "/browse",
    image: "📖"
  };
*/
  // Sidebar ads
  const sidebarAds = [
 /*    {
      id: 1,
      title: "Write Your Story",
      description: "Join 1,000+ writers",
      cta: "Start Writing",
      image: "✍️",
      link: "/write",
      color: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
    },*/
    {
      id: 2,
      title: "Support StoryMeal",
      description: "Help keep stories free",
      cta: "Buy us a side dish",
      image: "🍲",
      titleLink: "/appeal",
      buttonLink: "https://www.paypal.com/ncp/payment/WKSNR77WNYPFS",
      color: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)"
    }
  ];

  /**
   * Compute the slug path for the current story view (memoised).
   * Used as `path` for SEO meta tags so canonical/OG match the visible URL.
   */
  const storySlugPath = useMemo(() => {
    if (!selectedStory) return null;
    if (selectedStory.slug) return `/story/${selectedStory.slug}`;
    if (selectedStory.story_id) return `/story/${selectedStory.story_id}`;
    return null;
  }, [selectedStory]);

  /**
   * Compute ItemList JSON-LD for the current view (used for /browse, /genre/:name).
   */
  const listSchema = useMemo(() => {
    if (currentView !== 'read' || !stories || stories.length === 0) return null;
    return {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      itemListElement: stories.slice(0, 10).map((s, idx) => ({
        '@type': 'ListItem',
        position: idx + 1,
        url: `${SITE_URL}/story/${encodeURIComponent(s.slug || s.story_id)}`,
        name: s.title || 'Untitled'
      }))
    };
  }, [currentView, stories]);

  /**
   * Dynamic SEO props based on current view + selected story/author/genre.
   */
  const seoProps = (() => {
    const path = location.pathname;
    const baseProps = { path };

    // Helper to merge in optional ItemList schema
    const withList = (props) => ({
      ...props,
      schema: listSchema || props.schema
    });

    // 404-ish: no story was loaded but URL says /story/...
    if (path.startsWith('/story/') && !selectedStory) {
      return {
        ...baseProps,
        title: 'Story Not Found',
        description: 'The story you are looking for does not exist or has been removed.',
        noindex: true
      };
    }

    // Story page (ShortStory schema + BreadcrumbList)
    if (path.startsWith('/story/') && selectedStory && storySlugPath) {
      return {
        ...baseProps,
        path: storySlugPath,
        title: `${selectedStory.title} by ${selectedStory.author || 'Anonymous'} \u2014 ${selectedStory.genre || 'Story'} | StoryMeal`,
        description: (selectedStory.content_text || '').slice(0, 155).trim(),
        keywords: `${selectedStory.genre || 'fiction'}, ${selectedStory.author || 'anonymous'}, ${selectedStory.title}, free story, read online`,
        author: selectedStory.author || 'Anonymous',
        type: 'article',
        publishedTime: selectedStory.created_at || selectedStory.publish_date,
        modifiedTime: selectedStory.updated_at || selectedStory.created_at || selectedStory.publish_date,
        schema: storySeoExtra.main || buildStorySchema(selectedStory, storySlugPath)
      };
    }

    if (path === '/' || path === '') {
      return withList({
        ...baseProps,
        title: null,
        description: 'StoryMeal is a free online story platform where readers and writers meet. Discover thousands of stories across genres including Fantasy, Romance, Mystery, Horror, Science Fiction, Folk Tales, Fables, Poetry, and more.',
        keywords: 'stories, read stories, write stories, short stories, online stories, free stories, fantasy stories, romance stories, mystery stories, horror stories, science fiction, folk tales, fables, poetry, fan fiction, story writing, story platform, story community',
        schema: {
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: 'StoryMeal - Read, Write, and Share Stories',
          description: 'Free online story platform with thousands of stories across genres',
          url: `${SITE_URL}/`
        }
      });
    }

    if (path === '/browse') {
      return withList({
        ...baseProps,
        title: 'Browse All Stories',
        description: 'Browse thousands of free stories across all genres on StoryMeal. Find your next favorite read from our collection of fantasy, romance, mystery, horror, and more.',
        keywords: 'browse stories, all stories, story library, free stories online',
        schema: {
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: 'Browse All Stories',
          description: 'Browse thousands of free stories across all genres on StoryMeal.'
        }
      });
    }

    if (path.startsWith('/genre/')) {
      const genre = decodeURIComponent(path.replace('/genre/', ''));
      return withList({
        ...baseProps,
        title: `${genre} Stories \u2014 Free Online ${genre} Fiction | StoryMeal`,
        description: `Discover the best free ${genre} stories on StoryMeal. Read online ${genre} tales from our community of writers, sorted by popularity and recency.`,
        keywords: `${genre} stories, ${genre} fiction, free ${genre} stories, read ${genre} online, ${genre} tales`,
        schema: {
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: `${genre} Stories`,
          description: `Free ${genre} stories on StoryMeal.`
        }
      });
    }

    if (path === '/authors') {
      return {
        ...baseProps,
        title: 'Browse Authors',
        description: 'Discover talented writers on StoryMeal. Browse authors by name, story count, and popularity.',
        keywords: 'authors, writers, story writers, browse authors',
        schema: {
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: 'Browse Authors',
          description: 'Discover talented writers on StoryMeal.'
        }
      };
    }

    if (path.startsWith('/author/')) {
      const author = decodeURIComponent(path.replace('/author/', ''));
      return {
        ...baseProps,
        title: `Stories by ${author} | StoryMeal`,
        description: `Read all stories by ${author} on StoryMeal. Free fiction, fantasy, romance, and mystery tales by ${author}.`,
        keywords: `${author}, stories by ${author}, ${author} writer, ${author} author`,
        type: 'profile',
        schema: {
          '@context': 'https://schema.org',
          '@type': 'Person',
          name: author,
          url: `${SITE_URL}/author/${encodeURIComponent(author)}`
        }
      };
    }

    if (path === '/write') {
      return {
        ...baseProps,
        title: 'Write a Story',
        description: 'Write and publish your own story on StoryMeal. Share your tales with our community of readers. Free to publish, no paywalls.',
        keywords: 'write story, publish story, story writing, free publishing'
      };
    }

    if (path === '/competition') {
      return withList({
        ...baseProps,
        title: 'Yearly Writing Competition | StoryMeal',
        description: 'Join the StoryMeal yearly writing competition. Compete with writers worldwide for views and likes. Winner announced January 31st.',
        keywords: 'writing competition, story contest, writing contest, yearly competition',
        schema: {
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: 'StoryMeal Writing Competition',
          description: 'Yearly writing competition with rankings based on views and likes.'
        }
      });
    }

    if (path === '/privacy') {
      return {
        ...baseProps,
        title: 'Privacy Policy',
        description: 'StoryMeal Privacy Policy. Learn how we collect, use, and protect your personal information.',
        keywords: 'privacy policy, data protection, privacy'
      };
    }

    if (path === '/terms') {
      return {
        ...baseProps,
        title: 'Terms of Service',
        description: 'StoryMeal Terms of Service. Read our terms and conditions for using the platform.',
        keywords: 'terms of service, terms and conditions, user agreement'
      };
    }

    if (path === '/contact') {
      return {
        ...baseProps,
        title: 'Contact Us',
        description: 'Get in touch with the StoryMeal team. Reach out for support, advertising inquiries, privacy requests, DMCA notices, and more.',
        keywords: 'contact us, contact storymeal, support, help, advertising inquiries, privacy requests, dmca'
      };
    }

    if (path === '/appeal' || path === '/support') {
      return {
        ...baseProps,
        title: 'Support StoryMeal',
        description: 'Help keep StoryMeal free. Support our mission to make stories accessible to everyone, everywhere.',
        keywords: 'support, donate, help, keep stories free'
      };
    }

    if (path === '/admin' || path === '/author-dashboard') {
      return {
        ...baseProps,
        title: 'Dashboard',
        description: 'Manage your stories and account.',
        noindex: true
      };
    }

    return baseProps;
  })();

  // Also emit the breadcrumb as a separate schema in story view (multiple Helmet instances
  // with different schema props would normally collide, but we put the breadcrumb in the
  // same <script> block by extending the SEO's schema prop here).
  // To keep the Helmet output clean and singular per page, we just pass the breadcrumb as
  // an additional structured data blob via a second <SEO schema=...> only when on a story page.
  const breadcrumbSchema = (location.pathname.startsWith('/story/') && storySeoExtra.breadcrumb)
    ? storySeoExtra.breadcrumb
    : null;

  return (
    <div className="app">
      {/* Dynamic SEO Meta Tags (canonical + OG + schema) */}
      <SEO {...seoProps} />

      {/* Story-specific BreadcrumbList schema (only on story pages) */}
      {breadcrumbSchema && (
        <SEO schema={breadcrumbSchema} />
      )}

      {/* (top banner removed — see commit c189a26. The orphan JSX
          block that referenced `topBannerAd` was deleted in
          c189a26's follow-up; keeping this comment as a marker. */}

      {/* Login Modal */}
      {showLoginModal && (
        <div className="login-modal-overlay">
          <div className="login-modal" ref={modalRef}>
            <div className="login-modal-header">
              <h2>Welcome to StoryMeal</h2>
              <button className="modal-close" onClick={() => setShowLoginModal(false)}>✕</button>
            </div>
            <div className="login-modal-body">
              <p>Sign in to like stories and save your favorites</p>
              <AuthButtons onLogin={handleLogin} />
            </div>
          </div>
        </div>
      )}

      <header className="header" role="banner">
        <div className="header-content">
          <div className="logo-section">
            <h1 onClick={handleHomeClick} style={{ cursor: 'pointer' }}>
              <img
                src="/StoryMealLogo2.png"
                alt="StoryMeal logo"
                className="header-logo-icon"
                width="48"
                height="48"
              />
              <span className="header-logo-text">StoryMeal</span>
            </h1>
            <div className="powered-by">
              powered by <a href="https://austrasoft.com" target="_blank" rel="noopener noreferrer">Austrasoft.com</a>
            </div>
          </div>

          <nav className="nav-section" role="navigation" aria-label="Main navigation">
            <button onClick={handleHomeClick}>Home</button>
            <button onClick={handleBrowseClick}>Browse Stories</button>
            <button onClick={handleBrowseAuthorsClick}>Authors</button>
            <button onClick={handleCompetitionClick} className="competition-btn">🏆 Competition</button>
            <a
              href={PAYPAL_DONATE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="donate-btn"
              aria-label="Donate to StoryMeal via PayPal (opens in a new tab)"
            >
              💝 Donate
            </a>
            <button onClick={handleWriteClick} className="write-btn">
              ✍️ Write Story
            </button>
          </nav>

          <div className="auth-section">
            {!authLoading && (
              <>
                {user ? (
                  <div className="user-menu-container" ref={menuRef}>
                    <div className="user-info" onClick={() => setShowUserMenu(!showUserMenu)}>
                      {user.avatar_url ? (
                        <img src={user.avatar_url} alt={`${user.pet_name}'s avatar`} className="avatar" width="40" height="40" />
                      ) : (
                        <div className="avatar-placeholder" aria-label="User avatar">🐾</div>
                      )}
                      <span className="pet-name">{user.pet_name}</span>
                      <span className="dropdown-arrow">▼</span>
                    </div>
                    {showUserMenu && (
                      <div className="user-dropdown">
                        <div className="dropdown-header">
                          <strong>{user.pet_name}</strong>
                          <small>{user.email || user.pet_name + '@storymeal.com'}</small>
                        </div>
                        <div className="dropdown-divider"></div>
                        <button onClick={handleAuthorDashboardClick} className="dropdown-item">✍️ My Author Dashboard</button>
                        <div className="dropdown-divider"></div>
                        {user.email === 'sandip.kumar.kol@gmail.com' && (
                          <button onClick={handleAdminClick} className="dropdown-item">🛡️ Admin Dashboard</button>
                        )}
                        <div className="dropdown-divider"></div>
                        <button onClick={handleLogout} className="dropdown-item">🚪 Logout</button>
                      </div>
                    )}
                  </div>
                ) : (
                  <button onClick={handleLoginClick} className="login-btn">
                    🔑 Sign In
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </header>

      <div className="main-container">
        {/* Left Sidebar */}
        

        {/* Main Content */}
        <main className="content" role="main">

          {/* Genre dropdown (replaces the horizontal pills) */}
          <div className="genre-dropdown-row">
            <label htmlFor="genre-dropdown" className="genre-dropdown-label">
              Browse genre:
            </label>
            <select
              id="genre-dropdown"
              className="genre-dropdown"
              value={selectedGenre || ''}
              onChange={(e) => fetchStoriesByGenre(e.target.value || null)}
            >
              <option value="">📚 All genres</option>
              {genres.map(genre => (
                <option key={genre.name} value={genre.name}>
                  {iconFor(genre.name)} {genre.name}
                </option>
              ))}
            </select>
          </div>
          {currentView === 'admin' && (
            <AdminDashboard
              user={user}
              onBack={() => {
                setCurrentView('home');
                navigate('/');
                scrollToTop();
              }}
            />
          )}

          {currentView === 'home' && (
            <>
              <div className="story-sections">
                <section className="recent-section" aria-label="Recent stories">
                  <h2>📖 Recent Stories</h2>
                  <StoryList stories={recent} onViewStory={viewStory} onViewAuthor={handleViewAuthor} user={user} login={handleLoginClick} onDeleteStory={handleDeleteStory} />
                </section>
                <section className="trending-section" aria-label="Trending stories">
                  <h2>🔥 Trending Stories</h2>
                  <StoryList stories={trending} onViewStory={viewStory} onViewAuthor={handleViewAuthor} user={user} login={handleLoginClick} onDeleteStory={handleDeleteStory} />
                </section>
              </div>
            </>
          )}

          {currentView === 'read' && (
            <>
              <div className="stories-search-row">
                <input
                  type="search"
                  className="stories-search-input"
                  placeholder="🔍 Search stories by title, author, or genre..."
                  value={storySearchQuery}
                  onChange={(e) => setStorySearchQuery(e.target.value)}
                />
                {storySearchQuery && (
                  <button
                    className="stories-search-clear"
                    onClick={() => setStorySearchQuery('')}
                    title="Clear search"
                  >
                    ✕
                  </button>
                )}
              </div>
              {selectedGenre && (
                <div className="genre-header">
                  <h2>{selectedGenre} Stories</h2>
                  <button onClick={handleBrowseClick}>Clear Filter</button>
                </div>
              )}
              {filteredStories.length === 0 && storySearchQuery ? (
                <div className="admin-empty" style={{ padding: '40px', textAlign: 'center' }}>
                  <div style={{ fontSize: '2rem', marginBottom: '8px' }}>🔍</div>
                  <h3>No stories match "{storySearchQuery}"</h3>
                  <p style={{ color: '#666' }}>Try a different keyword, or clear the search.</p>
                </div>
              ) : (
                <StoryList stories={filteredStories} loading={loading} onViewStory={viewStory} onViewAuthor={handleViewAuthor} user={user} login={handleLoginClick} onDeleteStory={handleDeleteStory} />
              )}
            </>
          )}

          {currentView === 'write' && (
            <WriteStory
              user={user}
              onSuccess={() => {
                fetchRecent();
                fetchGenres();
                setCurrentView('home');
                navigate('/');
                scrollToTop();
              }}
            />
          )}

          {currentView === 'authors' && (
            <BrowseAuthors
              onViewAuthor={handleViewAuthor}
              onBack={() => {
                setCurrentView('home');
                navigate('/');
                scrollToTop();
              }}
            />
          )}

          {currentView === 'author-dashboard' && (
            <AuthorDashboard
              user={user}
              onBack={() => {
                setCurrentView('home');
                navigate('/');
                scrollToTop();
              }}
              onViewStory={viewStory}
              onUserUpdate={(updatedUser) => setUser(updatedUser)}
            />
          )}

          {currentView === 'author-stories' && selectedAuthor && (
            <AuthorStories
              author={selectedAuthor}
              onBack={() => {
                setSelectedAuthor(null);
                setCurrentView('home');
                navigate('/');
                scrollToTop();
              }}
              onViewStory={viewStory}
            />
          )}

          {currentView === 'story' && selectedStory && (
            <StoryViewer
              story={selectedStory}
              onBack={() => {
                setCurrentView('home');
                navigate('/');
                scrollToTop();
              }}
              onLikeUpdate={(updated) => setSelectedStory(updated)}
              user={user}
              login={handleLoginClick}
              onDeleteStory={handleDeleteStory}
            />
          )}

          {currentView === 'privacy' && (
            <PrivacyPolicy onBack={() => {
              setCurrentView('home');
              navigate('/');
              scrollToTop();
            }} />
          )}

          {currentView === 'terms' && (
            <TermsOfService onBack={() => {
              setCurrentView('home');
              navigate('/');
              scrollToTop();
            }} />
          )}

          {currentView === 'contact' && (
            <ContactUs onBack={() => {
              setCurrentView('home');
              navigate('/');
              scrollToTop();
            }} />
          )}

          {currentView === 'competition' && (
            <Competition
              onBack={() => {
                setCurrentView('home');
                navigate('/');
                scrollToTop();
              }}
              onViewAuthor={handleViewAuthor}
              onViewStory={viewStory}
            />
          )}

          {currentView === 'appeal' && (
            <AppealPage onBack={() => {
              setCurrentView('home');
              navigate('/');
              scrollToTop();
            }} />
          )}
        </main>

        {/* Right Sidebar */}
        <aside className="right-sidebar" role="complementary" aria-label="Sponsored content and stats">
          <div className="ads-section">
            <h3>Sponsored</h3>
            {sidebarAds.map(ad => (
            <div key={ad.id} className="ad-card" style={{ background: ad.color }}>
              <div className="ad-icon">{ad.image}</div>
              {ad.titleLink === '/appeal' ? (
                <button onClick={handleAppealClick} className="ad-title-link">
                  <h4>{ad.title}</h4>
                </button>
              ) : ad.titleLink ? (
                <a href={ad.titleLink} className="ad-title-link">
                  <h4>{ad.title}</h4>
                </a>
              ) : (
                <h4>{ad.title}</h4>
              )}
              <p>{ad.description}</p>
              {ad.buttonLink ? (
                <a
                  href={ad.buttonLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ad-button"
                >
                  {ad.cta} →
                </a>
              ) : (
                <a href={ad.link} className="ad-button" onClick={(e) => { e.preventDefault(); if (ad.link === '/write') handleWriteClick(); else if (ad.link === '/browse') handleBrowseClick(); }}>{ad.cta} →</a>
              )}
            </div>
          ))}
          </div>

          <div className="info-section">
            <h3>📊 StoryMeal Stats</h3>
            <div className="stat-item">
              <span>📖 Total Stories:</span>
              <strong>{siteStats.total_stories}</strong>
            </div>
            <div className="stat-item">
              <span>👥 Active Readers:</span>
             <strong>{(siteStats.active_readers ?? 0).toLocaleString()}</strong>
            </div>
            <div className="stat-item">
              <span>✍️ Writers:</span>
              <strong>{siteStats.writers}</strong>
            </div>
            <div className="stat-item">
              <span>🏷️ Genres:</span>
              <strong>{genres.length}</strong>
            </div>
          </div>
        </aside>
      </div>

      {/* Footer */}
      <Footer onPrivacyClick={handlePrivacyClick} onTermsClick={handleTermsClick} onContactClick={handleContactClick} />

      {/* Floating YouTube player — fixed bottom-right, overlays all views.
          Was previously inline in the middle of the home page; now removed
          from there and re-rendered here as a small fixed card. Users can
          dismiss with the ✕ button (closes for the session). */}
      <YouTubePlayer
        position="floating"
        videoId="g0ZG7e8e00k"
        title="Panchatantra Stories"
      />
    </div>
  );
}

export default App;